/** Shared Sentry options for client, server, and edge runtimes. */

// Static process.env access is required so Next.js can inline NEXT_PUBLIC_* in client bundles.
export const SENTRY_DSN =
  process.env.NEXT_PUBLIC_SENTRY_DSN?.trim() || process.env.SENTRY_DSN?.trim() || '';

export const SENTRY_REPOSITORY =
  process.env.NEXT_PUBLIC_SENTRY_REPOSITORY?.trim() ||
  process.env.SENTRY_REPOSITORY?.trim() ||
  'scoville/scoville-realtime-interpretation-livekit';

export function sentryEnvironment() {
  return (
    process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
    process.env.SENTRY_ENVIRONMENT ??
    process.env.NODE_ENV ??
    'development'
  );
}

/** Deploy stage tag (e.g. production). Falls back to Sentry environment. */
export function sentryStage() {
  return process.env.NEXT_PUBLIC_SENTRY_STAGE ?? process.env.SENTRY_STAGE ?? sentryEnvironment();
}

/** Git commit hash used as the Sentry release identity. */
export function sentryRelease() {
  return (
    process.env.NEXT_PUBLIC_SENTRY_RELEASE ??
    process.env.SENTRY_RELEASE ??
    process.env.GITHUB_SHA ??
    'unknown'
  );
}

export function sentryTracesSampleRate() {
  const fallback = process.env.NODE_ENV === 'development' ? 1.0 : 0.1;
  const raw = process.env.SENTRY_TRACES_SAMPLE_RATE;
  if (!raw) {
    return fallback;
  }
  const rate = Number(raw);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    return fallback;
  }
  return rate;
}

/** Tags attached to every event from this runtime. */
export function sentryTags() {
  return {
    repository: SENTRY_REPOSITORY,
    stage: sentryStage(),
    release: sentryRelease(),
  };
}

const LIVEKIT_DATA_CHANNEL_CLOSE_RE =
  /publisher data channel '_(?:reliable|lossy|data_track)' closed unexpectedly/i;

function eventText(event: { message?: string; title?: string; logentry?: { message?: string } }) {
  const parts: string[] = [];
  if (event.logentry?.message) {
    parts.push(event.logentry.message);
  }
  if (event.message) {
    parts.push(event.message);
  }
  if (event.title) {
    parts.push(event.title);
  }
  return parts.join(' ');
}

/** Benign LiveKit RTC data-channel teardown noise (see Sentry issue 7615283242). */
export function isLivekitDataChannelDisconnectNoise(event: {
  message?: string;
  title?: string;
  logger?: string;
  logentry?: { message?: string };
}) {
  const combined = eventText(event);
  if (LIVEKIT_DATA_CHANNEL_CLOSE_RE.test(combined)) {
    return true;
  }
  return (
    (event.logger?.includes('rtc_session') ?? false) &&
    combined.toLowerCase().includes('data channel') &&
    combined.toLowerCase().includes('closed unexpectedly')
  );
}

export function sentryBeforeSend<
  T extends { message?: string; title?: string; logger?: string; logentry?: { message?: string } },
>(event: T) {
  if (isLivekitDataChannelDisconnectNoise(event)) {
    return null;
  }
  return event;
}
